import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { toast } from 'react-hot-toast';
import adminApi from '../services/api';
import BottomNav from '../components/BottomNav';

// Register ChartJS components with all required plugins
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Constants
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000; // 1 second

const Dashboard = () => {
  const toastShownRef = useRef(false);
  const retryCountRef = useRef(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isEmptyData, setIsEmptyData] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [stats, setStats] = useState({
    // Today's stats
    todaySales: 0,
    todayOrders: 0,
    todayAverageOrder: 0,

    // Total stats
    totalSales: 0,
    totalOrders: 0,
    averageOrderValue: 0,

    // Comparison metrics
    revenueChange: 0,
    lastMonthRevenue: 0,
    orderChange: 0,
  });

  const [recentOrders, setRecentOrders] = useState([]);

  const [salesData, setSalesData] = useState({
    labels: [],
    datasets: [{
      label: 'Sales (₹)',
      data: [],
      borderColor: '#06b6d4',
      backgroundColor: 'rgba(6, 182, 212, 0.2)',
      tension: 0.4,
      borderWidth: 2,
      fill: true,
    }],
  });

  // Memoized chart options
  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index',
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(75, 85, 99, 0.5)',
        },
        ticks: {
          color: '#e5e7eb',
          callback: (value) => `₹${value.toLocaleString()}`
        },
        title: {
          display: true,
          text: 'Amount (₹)',
          color: '#9ca3af',
        }
      },
      x: {
        grid: {
          color: 'rgba(75, 85, 99, 0.2)',
        },
        ticks: {
          color: '#9ca3af',
          maxRotation: 45,
          minRotation: 45,
          padding: 10,
        },
        title: {
          display: true,
          text: 'Day of Month',
          color: '#9ca3af',
        }
      },
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#e5e7eb',
          padding: 20,
          font: {
            size: 12,
          },
        }
      },
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.9)',
        titleColor: '#ffffff',
        bodyColor: '#e5e7eb',
        borderColor: 'rgba(75, 85, 99, 0.5)',
        borderWidth: 1,
        padding: 12,
        callbacks: {
          label: (context) => `₹${context.raw.toLocaleString()}`,
          title: (items) => {
            if (!items.length) return '';
            return `Day ${items[0].label}`;
          }
        }
      }
    },
    elements: {
      point: {
        radius: 3,
        hoverRadius: 6,
        backgroundColor: '#06b6d4',
        borderColor: '#ffffff',
        borderWidth: 2,
      },
      line: {
        tension: 0.3,
        borderWidth: 2,
      }
    },
  }), []);

  // Define fetchDashboardData first since it's used in loadData
  const fetchDashboardData = useCallback(async () => {
    try {
      setHasError(false);
      setIsEmptyData(false);

      console.log('Fetching dashboard data...');
      const response = await adminApi.dashboard.getStats();
      console.log('Dashboard API response:', response);

      // Check if we got a valid response
      if (!response) {
        throw new Error('No response from dashboard API');
      }

      // Extract data from response (handle both direct and nested data)
      const dashboardData = response.data || response;

      if (!dashboardData) {
        throw new Error('Invalid dashboard data format');
      }

      console.log('Dashboard data:', dashboardData);

      // Check if we have any meaningful data
      if ((dashboardData.totalOrders === 0 || !dashboardData.totalOrders) &&
        (dashboardData.totalSales === 0 || !dashboardData.totalSales)) {
        console.log('No order data available');
        setIsEmptyData(true);
        setLastUpdated(new Date());
        return;
      }

      setLastUpdated(new Date());

      // Format currency values
      const formatCurrency = (amount) => {
        const value = typeof amount === 'number' ? amount : parseFloat(amount) || 0;
        return new Intl.NumberFormat('en-IN', {
          style: 'currency',
          currency: 'INR',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(value).replace('₹', '₹ ');
      };

      // Get total sales value, ensuring it's a number
      const totalSalesValue = typeof dashboardData.totalSales === 'number'
        ? dashboardData.totalSales
        : parseFloat(dashboardData.totalSales) || 0;

      // Derive today's stats from salesTrend when available
      const trend = dashboardData.salesTrend || [];
      const todayEntry = trend.length ? trend[trend.length - 1] : { total: 0, count: 0 };
      const prevEntry = trend.length > 1 ? trend[trend.length - 2] : { total: 0, count: 0 };
      const todaySalesRaw = Number(todayEntry.total) || 0;
      const todayOrdersRaw = Number(todayEntry.count) || 0;
      const todayAovRaw = todayOrdersRaw > 0 ? todaySalesRaw / todayOrdersRaw : 0;
      const orderChangePct = prevEntry.count > 0
        ? ((todayOrdersRaw - Number(prevEntry.count)) / Number(prevEntry.count)) * 100
        : 0;

      // Update stats with dashboard data
      setStats({
        // Today's stats
        todaySales: todaySalesRaw,
        todayOrders: todayOrdersRaw,
        todayAverageOrder: todayAovRaw,

        // Total stats
        totalSales: formatCurrency(totalSalesValue),
        totalOrders: dashboardData.totalOrders || 0,
        averageOrderValue: formatCurrency(dashboardData.avgOrderValue || 0),

        // Comparison metrics
        revenueChange: 0,
        orderChange: orderChangePct,
        lastMonthRevenue: formatCurrency(0),
      });

      // Set recent orders (use recentTransactions from dashboard data or empty array)
      const recentOrders = (dashboardData.recentTransactions || []).slice(0, 5).map(transaction => ({
        id: transaction.id || `order-${Math.random().toString(36).substr(2, 9)}`,
        customer: transaction.customerName || transaction.user?.name || 'Guest',
        amount: transaction.amount || transaction.price || 0,
        status: transaction.status || 'completed',
        date: transaction.date || new Date().toLocaleDateString(),
      }));

      console.log('Setting recent orders:', recentOrders);
      setRecentOrders(recentOrders);

      // Process sales trend data for chart
      const salesTrend = dashboardData.salesTrend || [];
      console.log('Sales trend data:', salesTrend);

      if (salesTrend.length > 0) {
        // Format data for chart
        const labels = salesTrend.map(item => {
          try {
            const date = new Date(item.date || item.id);
            return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
          } catch (e) {
            console.error('Error formatting date:', e);
            return 'N/A';
          }
        });

        const data = salesTrend.map(item => item.total || 0);

        console.log('Setting chart data - labels:', labels, 'data:', data);

        setSalesData(prev => ({
          ...prev,
          labels: labels,
          datasets: [{
            ...prev.datasets[0],
            data: data,
            label: 'Daily Sales (₹)',
          }],
        }));
      } else {
        console.log('No sales trend data, setting empty chart');
        // Set empty chart data
        setSalesData(prev => ({
          ...prev,
          labels: [],
          datasets: [{
            ...prev.datasets[0],
            data: [],
            label: 'Daily Sales (₹)',
          }],
        }));
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      setHasError(true);
      throw error;
    }
  }, []);

  // Now define loadData which uses fetchDashboardData
  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      await fetchDashboardData();
      retryCountRef.current = 0; // Reset retry count on success
    } catch (error) {
      console.error('Failed to load dashboard data:', error);

      // Implement exponential backoff for retries
      if (retryCountRef.current < MAX_RETRIES) {
        const delay = INITIAL_RETRY_DELAY * Math.pow(2, retryCountRef.current);
        retryCountRef.current += 1;

        setTimeout(() => {
          loadData();
        }, delay);
        return;
      }

      setHasError(true);
      if (!toastShownRef.current) {
        const errorMessage = error.response?.data?.message || 'Failed to load dashboard data';
        toast.error(errorMessage);
        toastShownRef.current = true;
      }
    } finally {
      setIsLoading(false);
    }
  }, [fetchDashboardData]);

  useEffect(() => {
    loadData();

    // Set up auto-refresh every 30 seconds
    const interval = setInterval(loadData, 30000);

    return () => {
      clearInterval(interval);
      toastShownRef.current = false;
    };
  }, [loadData]);

  const handleRefresh = async () => {
    try {
      setIsLoading(true);
      await fetchDashboardData();
      toast.success('Dashboard data refreshed');
    } catch (error) {
      console.error('Failed to refresh dashboard data:', error);
      toast.error('Failed to refresh data');
    } finally {
      setIsLoading(false);
    }
  };

  // Loading skeleton component
  const LoadingSkeleton = () => (
    <div className="space-y-6 p-4">
      <div className="flex justify-between items-center">
        <div className="h-10 bg-gray-800 rounded w-64"></div>
        <div className="h-10 bg-gray-800 rounded w-32"></div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[1, 2].map((i) => (
          <div key={i} className="bg-gray-900/80 rounded-xl p-6 h-40 animate-pulse"></div>
        ))}
      </div>
      <div className="bg-gray-900/80 rounded-xl p-6 h-96 animate-pulse"></div>
      <div className="bg-gray-900/80 rounded-xl p-6 h-96 animate-pulse"></div>
    </div>
  );

  if (isLoading && !recentOrders.length) {
    return <LoadingSkeleton />;
  }

  if (hasError && !recentOrders.length) {
    return (
      <div className="flex flex-col items-center justify-center h-screen space-y-4 p-4">
        <div className="bg-red-900/50 border border-red-700 rounded-lg p-6 text-center max-w-md">
          <h2 className="text-xl font-bold text-red-400 mb-2">Error Loading Dashboard</h2>
          <p className="text-red-200 mb-4">
            {retryCountRef.current >= MAX_RETRIES
              ? 'Maximum retry attempts reached. Please check your connection and try again.'
              : 'We couldn\'t load the dashboard data. Please try again.'
            }
          </p>
          <button
            onClick={loadData}
            disabled={isLoading}
            className={`bg-red-700 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors ${isLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
          >
            {isLoading ? 'Retrying...' : 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  if (isEmptyData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] p-6 text-center">
        <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-8 max-w-md w-full">
          <div className="text-6xl mb-4">📊</div>
          <h2 className="text-2xl font-bold text-gray-200 mb-2">No Data Available</h2>
          <p className="text-gray-400 mb-6">
            There's no order data to display in the dashboard yet. Check back later or process some orders.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={loadData}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              Refresh Data
            </button>
            <a
              href="/orders"
              className="bg-gray-800 hover:bg-gray-700 text-white px-6 py-2 rounded-lg transition-colors text-center"
            >
              View Orders
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 bg-black min-h-screen pb-20 md:pb-6 text-white">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
            Dashboard Overview
          </h1>
          {lastUpdated && (
            <p className="text-xs text-gray-400 mt-1">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {isLoading && (
            <span className="text-sm text-cyan-400">
              Updating...
            </span>
          )}
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className={`flex items-center gap-2 bg-cyan-600 text-white px-6 py-2.5 rounded-lg transition-all duration-200 w-full md:w-auto shadow-lg hover:shadow-cyan-500/25 ${isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:bg-cyan-500'
              }`}
          >
            <svg
              className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Refresh Data
          </button>
        </div>
      </div>

      {/* Stats Grid - Today's Overview */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-white mb-4">Today's Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6 text-white">
          {/* Today's Sales */}
          <div className="bg-black p-4 md:p-6 rounded-xl border border-gray-700 hover:border-emerald-500 transition-all duration-300 group">
            <div className="flex justify-between items-start">
              <h3 className="text-gray-300 text-sm font-medium">Today's Earning</h3>
              <div className="bg-emerald-900/50 text-emerald-300 text-xs px-2 py-1 rounded-full border border-emerald-700 group-hover:border-emerald-500">
                {stats.todayOrders} orders
              </div>
            </div>
            <p className="text-2xl md:text-3xl font-bold text-white mt-2">₹{stats.todaySales.toLocaleString()}</p>
            <p className="text-xs md:text-sm text-gray-400 mt-1">
              {new Date().toLocaleDateString('en-IN', { weekday: 'long', month: 'short', day: 'numeric' })}
            </p>
          </div>

          {/* Today's Orders */}
          <div className="bg-gray-900/80 backdrop-blur-sm p-4 md:p-6 rounded-xl border border-gray-800 hover:border-blue-500/50 transition-all duration-300 group">
            <div className="flex justify-between items-start">
              <h3 className="text-gray-300 text-sm font-medium">Today's Orders</h3>
              <div className="bg-blue-900/50 text-blue-300 text-xs px-2 py-1 rounded-full border border-blue-700 group-hover:border-blue-500">
                Today
              </div>
            </div>
            <p className="text-2xl md:text-3xl font-bold text-white mt-2">{stats.todayOrders}</p>
            <div className="flex items-center mt-1">
              <span className={`text-xs md:text-sm flex items-center ${stats.orderChange >= 0 ? 'text-emerald-400' : 'text-red-400'
                }`}>
                <span className="w-2 h-2 rounded-full mr-2 animate-pulse" style={{ backgroundColor: stats.orderChange >= 0 ? '#34d399' : '#f87171' }}></span>
                {stats.orderChange >= 0 ? '↑' : '↓'} {Math.abs(stats.orderChange).toFixed(1)}%
              </span>
              <span className="text-xs md:text-sm text-gray-400 ml-2">vs yesterday</span>
            </div>
          </div>


        </div>
      </div>

      {/* Stats Grid - Total Overview */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-white mb-4">Total Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6 text-white">
          {/* Total Sales */}
          <div className="bg-black p-4 md:p-6 rounded-xl border border-gray-700 hover:border-emerald-500 transition-all duration-300 group">
            <div className="flex justify-between items-start">
              <h3 className="text-gray-300 text-sm font-medium">Total Sales Earning</h3>
              <div className="bg-emerald-900/50 text-emerald-300 text-xs px-2 py-1 rounded-full border border-emerald-700 group-hover:border-emerald-500">
                All Time
              </div>
            </div>
            <p className="text-2xl md:text-3xl font-bold text-white mt-2">{stats.totalSales}</p>
            <div className="flex items-center mt-1">
              <span className={`text-xs md:text-sm flex items-center ${stats.revenueChange >= 0 ? 'text-emerald-400' : 'text-red-400'
                }`}>
                <span className="w-2 h-2 rounded-full mr-2 animate-pulse" style={{ backgroundColor: stats.revenueChange >= 0 ? '#34d399' : '#f87171' }}></span>
                {stats.revenueChange >= 0 ? '↑' : '↓'} {Math.abs(stats.revenueChange).toFixed(1)}%
              </span>
              <span className="text-xs md:text-sm text-gray-400 ml-2">vs last month</span>
            </div>
          </div>

          {/* Total Orders */}
          <div className="bg-gray-900/80 backdrop-blur-sm p-4 md:p-6 rounded-xl border border-gray-800 hover:border-blue-500/50 transition-all duration-300 group">
            <div className="flex justify-between items-start">
              <h3 className="text-gray-300 text-sm font-medium">Total Orders</h3>
              <div className="bg-blue-900/50 text-blue-300 text-xs px-2 py-1 rounded-full border border-blue-700 group-hover:border-blue-500">
                All Time
              </div>
            </div>
            <p className="text-2xl md:text-3xl font-bold text-white mt-2">{stats.totalOrders}</p>
            <p className="text-xs md:text-sm text-blue-400 mt-1 flex items-center">
              <span className="w-2 h-2 rounded-full bg-blue-400 mr-2 animate-pulse"></span>
              All orders processed
            </p>
          </div>


        </div>
      </div>

      {/* Sales Chart */}
      <div className="bg-black p-4 md:p-6 rounded-xl border border-gray-700 mb-6 md:mb-8">
        <h2 className="text-lg md:text-xl font-semibold mb-4 text-white">Sales Trend</h2>
        <div className="h-64 md:h-80">
          {salesData.labels.length > 0 ? (
            <Line data={salesData} options={chartOptions} />
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-400">No sales data available</p>
            </div>
          )}
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-black p-4 md:p-6 rounded-xl border border-gray-700 mb-6">
        <h2 className="text-lg md:text-xl font-semibold mb-4 text-white">Recent Orders</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Order ID
                </th>
                <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {recentOrders.map((order) => (
                <tr className="hover:bg-gray-900 transition-colors duration-150">
                  <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm text-white">
                    #{order.id.slice(-6)}
                  </td>
                  <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm text-white">
                    {order.customer}
                  </td>
                  <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                    ₹{order.amount.toLocaleString()}
                  </td>
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          (s => {
                            const status = String(s).toUpperCase();
                            if (['FULFILLED', 'COMPLETED', 'DELIVERED'].includes(status)) return "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20";
                            if (['CAPTURED', 'AUTHORIZED'].includes(status)) return "bg-blue-500/10 text-blue-400 border border-blue-500/20";
                            if (['FAILED'].includes(status)) return "bg-red-500/10 text-red-400 border border-red-500/20";
                            if (['CREATED', 'PENDING', 'PROCESSING'].includes(status)) return "bg-amber-500/10 text-amber-400 border border-amber-500/20";
                            return "bg-slate-500/10 text-slate-400 border border-slate-500/20";
                          })(order.status)
                        }`}>
                          {order.status}
                        </span>
                      </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default Dashboard;